import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';

import { AlertContext } from '../contexts/alert-context';
import type { Store } from '../data/store';
import { storesQueryOptions } from '../query/query-options';
import { useStoreService } from './use-store-service';

export const useStores = () => {
  const { addErrorAlert } = useContext(AlertContext);
  const queryClient = useQueryClient();
  const storeService = useStoreService();

  const { data: stores = [], isLoading } = useQuery({
    ...storesQueryOptions(),
    queryFn: () => {
      return storeService
        .getAll()
        .then((stores: Store[]) => {
          if (stores.length === 0) {
            console.warn('No stores found');
          }

          return stores;
        })
        .catch((error: Error) => {
          addErrorAlert(error.message, { cause: error });

          throw error;
        });
    },
  });

  const updateStoreMutation = useMutation({
    mutationFn: async (store: Store) => {
      return storeService.update(store.id, store);
    },
    onSuccess: (updatedStore: Store) => {
      // Update the stores query cache
      queryClient.setQueryData(['stores'], (oldStores: Store[] = []) => {
        return oldStores.map((s) => (s.id === updatedStore.id ? updatedStore : s));
      });
    },
    onError: (error: Error) => {
      addErrorAlert(error.message, { cause: error });
    },
  });

  const updateStore = useCallback(
    (store: Store) => {
      return updateStoreMutation.mutateAsync(store);
    },
    [updateStoreMutation]
  );

  const hasData = stores.length > 0;
  const shouldShowLoading = (!hasData && isLoading) || updateStoreMutation.isPending;

  return {
    loading: shouldShowLoading,
    stores,
    updateStore,
  };
};
